/**
 * AdminQuestionsTab Component
 * Question management tab for admin panel with full multi-language support
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Upload, Download, Search, Loader2, X,
  CheckCircle, AlertCircle, HelpCircle, XCircle, Languages, Globe, Wand2, BookOpen, Eye
} from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import CSVColumnMapper from './CSVColumnMapper';
import QuestionRenderer from '@/components/QuestionRenderer';

const API = (window.location.origin || '') + '/api';

// All supported languages for the application (25 languages)
const LANGUAGES = [
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'cs', name: 'Czech', flag: '🇨🇿' },
  { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
  { code: 'el', name: 'Greek', flag: '🇬🇷' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'bg', name: 'Bulgarian', flag: '🇧🇬' },
  { code: 'hr', name: 'Croatian', flag: '🇭🇷' },
  { code: 'sk', name: 'Slovak', flag: '🇸🇰' },
  { code: 'sr', name: 'Serbian', flag: '🇷🇸' },
  { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
  { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' }
];

// Helper to transform API question format to form format
// Converts choices[].explanation and distractor_explanations to incorrect_explanations
const normalizeQuestionForForm = (question) => {
  if (!question) return question;
  
  const normalized = { ...question };
  
  // FIX: Ensure correct_answers is populated from choices[].is_correct
  if ((!normalized.correct_answers || normalized.correct_answers.length === 0) && normalized.choices?.length) {
    normalized.correct_answers = normalized.choices
      .filter(c => c.is_correct)
      .map(c => c.id);
  }
  
  // Extract correct justification from new formats
  if (!normalized.correct_justification) {
    // From choices
    if (normalized.choices?.length) {
      const correctChoice = normalized.choices.find(c => c.is_correct);
      if (correctChoice?.explanation) {
        normalized.correct_justification = correctChoice.explanation;
      }
    }
    // From structured_explanation
    if (!normalized.correct_justification && normalized.structured_explanation?.correct_answer_rationale) {
      normalized.correct_justification = normalized.structured_explanation.correct_answer_rationale;
    }
    // From correct_answer_explanation
    if (!normalized.correct_justification && normalized.correct_answer_explanation) {
      normalized.correct_justification = normalized.correct_answer_explanation;
    }
  }
  
  // Extract incorrect explanations from new formats
  if (!normalized.incorrect_explanations || Object.keys(normalized.incorrect_explanations).length === 0) {
    normalized.incorrect_explanations = {};
    
    // From choices[].explanation - only for non-correct choices
    if (normalized.choices?.length) {
      normalized.choices.forEach((choice, idx) => {
        if (!choice.is_correct && choice.explanation) {
          const label = choice.id || String.fromCharCode(65 + idx);
          normalized.incorrect_explanations[label] = choice.explanation;
        }
      });
      if (Object.keys(normalized.incorrect_explanations).length > 0) {
      }
    }
    
    // From structured_explanation.why_distractors_wrong
    if (Object.keys(normalized.incorrect_explanations).length === 0 && 
        normalized.structured_explanation?.why_distractors_wrong) {
      Object.entries(normalized.structured_explanation.why_distractors_wrong).forEach(([key, value]) => {
        normalized.incorrect_explanations[key] = value;
      });
    }
    
    // From distractor_explanations array
    if (Object.keys(normalized.incorrect_explanations).length === 0 && 
        normalized.distractor_explanations?.length) {
      normalized.distractor_explanations.forEach((expl, idx) => {
        const label = String.fromCharCode(66 + idx); // B, C, D, E
        normalized.incorrect_explanations[label] = expl;
      });
    }
  }
  
  return normalized;
};

// Helper to get translation status for a question
const getTranslationStatus = (question) => {
  const status = {};
  let translatedCount = 0;
  
  LANGUAGES.forEach(lang => {
    const hasText = question[`question_text_${lang.code}`] && question[`question_text_${lang.code}`].trim();
    status[lang.code] = hasText;
    if (hasText) translatedCount++;
  });
  
  return { status, translatedCount, total: LANGUAGES.length };
};

// Translation Status Badge Component
const TranslationStatusBadge = ({ question }) => {
  const { status, translatedCount, total } = getTranslationStatus(question);
  const percentage = Math.round((translatedCount / total) * 100);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 cursor-help">
            <Globe className={`w-4 h-4 ${translatedCount === total ? 'text-green-500' : translatedCount > 0 ? 'text-orange-500' : 'text-slate-400'}`} />
            <span className={`text-xs font-medium ${translatedCount === total ? 'text-green-600' : translatedCount > 0 ? 'text-orange-600' : 'text-slate-500'}`}>
              {translatedCount}/{total}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="p-3">
          <p className="font-semibold mb-2">Translation Status ({percentage}%)</p>
          <div className="grid grid-cols-3 gap-2">
            {LANGUAGES.map(lang => (
              <div key={lang.code} className="flex items-center gap-1 text-sm">
                <span>{lang.flag}</span>
                {status[lang.code] ? (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                ) : (
                  <XCircle className="w-3 h-3 text-red-400" />
                )}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Question Form Component with full multi-language support
const QuestionForm = ({ question, setQuestion, categories, getAuthHeaders, onTranslateComplete }) => {
  const [translating, setTranslating] = useState(false);
  const [expandedLangs, setExpandedLangs] = useState(false);

  const updateChoice = (index, field, value) => {
    const newChoices = [...question.choices];
    newChoices[index] = { ...newChoices[index], [field]: value };
    setQuestion({ ...question, choices: newChoices });
  };

  const addChoice = () => {
    const newId = `c${question.choices.length}`;
    const newChoices = [...question.choices, { id: newId, text: '', text_de: '', text_es: '', text_fr: '', text_zh: '', text_ru: '', text_it: '' }];
    setQuestion({ ...question, choices: newChoices });
  };

  const removeChoice = (index) => {
    if (question.choices.length <= 2) return;
    const removedId = question.choices[index].id;
    const newChoices = question.choices.filter((_, i) => i !== index);
    const newCorrectAnswers = (question.correct_answers || []).filter(id => id !== removedId);
    setQuestion({ ...question, choices: newChoices, correct_answers: newCorrectAnswers });
  };

  const toggleCorrectAnswer = (choiceId) => {
    const current = question.correct_answers || [];
    if (question.question_type === 'single') {
      setQuestion({ ...question, correct_answers: [choiceId] });
    } else {
      if (current.includes(choiceId)) {
        setQuestion({ ...question, correct_answers: current.filter(id => id !== choiceId) });
      } else {
        setQuestion({ ...question, correct_answers: [...current, choiceId] });
      }
    }
  };

  const updateMatchPair = (index, side, value) => {
    const newPairs = [...(question.match_pairs || [])];
    newPairs[index] = { ...newPairs[index], [side]: value };
    setQuestion({ ...question, match_pairs: newPairs });
  };

  const addMatchPair = () => {
    const newPairs = [...(question.match_pairs || []), { left: '', right: '' }];
    setQuestion({ ...question, match_pairs: newPairs });
  };

  const removeMatchPair = (index) => {
    const newPairs = (question.match_pairs || []).filter((_, i) => i !== index);
    setQuestion({ ...question, match_pairs: newPairs });
  };

  // Translate single question
  const handleTranslateQuestion = async () => {
    if (!question.question_id) {
      toast.error('Please save the question first before translating');
      return;
    }
    
    setTranslating(true);
    try {
      const response = await fetch(`${API}/translate/single/${question.question_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both immediate completion and background processing
        if (data.status === 'started') {
          toast.success(`Translation started for ${data.languages_to_translate?.length || 0} languages. Check progress in a moment.`);
          // Poll for completion after a delay
          setTimeout(async () => {
            try {
              const progressRes = await fetch(`${API}/translate/progress`, {
                headers: getAuthHeaders()
              });
              if (progressRes.ok) {
                const progress = await progressRes.json();
                if (progress.status === 'completed' || progress.completed > 0) {
                  // Refresh the question data
                  if (onTranslateComplete) {
                    const refreshRes = await fetch(`${API}/questions/${question.question_id}`, {
                      headers: getAuthHeaders()
                    });
                    if (refreshRes.ok) {
                      const refreshedData = await refreshRes.json();
                      onTranslateComplete(refreshedData);
                    }
                  }
                }
              }
            } catch (e) {
              console.error('Error checking translation progress:', e);
            }
          }, 5000); // Check after 5 seconds
        } else if (data.languages_translated) {
          toast.success(`Question translated to ${data.languages_translated.length} languages`);
          if (onTranslateComplete) {
            onTranslateComplete(data.question);
          }
        } else {
          toast.success(data.message || 'Translation completed');
        }
      } else if (response.status === 429) {
        // Translation already in progress - this is not an error
        const data = await response.json();
        toast.info(data.detail || 'Translation already in progress. Please wait.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Translation failed');
      }
    } catch (error) {
      toast.error('Failed to translate question');
    } finally {
      setTranslating(false);
    }
  };

  // Get current translation status
  const { status: transStatus, translatedCount } = getTranslationStatus(question);

  return (
    <div className="space-y-4">
      {/* Translation Status & Button */}
      {question.question_id && (
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#0047AB]" />
            <div>
              <p className="text-sm font-medium">Translation Status</p>
              <div className="flex items-center gap-2 mt-1">
                {LANGUAGES.map(lang => (
                  <span 
                    key={lang.code} 
                    className={`text-sm ${transStatus[lang.code] ? 'opacity-100' : 'opacity-40'}`}
                    title={`${lang.name}: ${transStatus[lang.code] ? 'Translated' : 'Missing'}`}
                  >
                    {lang.flag}
                  </span>
                ))}
                <span className="text-xs text-slate-500 ml-1">({translatedCount}/{LANGUAGES.length})</span>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleTranslateQuestion}
            disabled={translating}
            data-testid="translate-question-btn"
          >
            {translating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Translating...
              </>
            ) : (
              <>
                <Languages className="w-4 h-4 mr-2" />
                Translate
              </>
            )}
          </Button>
        </div>
      )}

      {/* Internal ID */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          Internal ID
          <span className="text-xs text-slate-500">(z.B. CAPM-0001)</span>
        </Label>
        <Input
          value={question.internal_id || ''}
          onChange={(e) => setQuestion({ ...question, internal_id: e.target.value })}
          placeholder="Auto-generated if empty"
          className="font-mono"
          data-testid="internal-id-input"
        />
      </div>

      {/* Question Text - English (Required) */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="text-lg">🇬🇧</span>
          Question Text (English) <span className="text-red-500">*</span>
        </Label>
        <Textarea
          value={question.question_text}
          onChange={(e) => setQuestion({ ...question, question_text: e.target.value })}
          rows={3}
          placeholder="Enter question in English..."
          data-testid="question-text-input"
        />
      </div>

      {/* Question Text - German */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="text-lg">🇩🇪</span>
          Question Text (German)
        </Label>
        <Textarea
          value={question.question_text_de || ''}
          onChange={(e) => setQuestion({ ...question, question_text_de: e.target.value })}
          rows={3}
          placeholder="Deutsche Übersetzung eingeben..."
          data-testid="question-text-de-input"
        />
      </div>

      {/* Question Text - Spanish */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <span className="text-lg">🇪🇸</span>
          Question Text (Spanish)
        </Label>
        <Textarea
          value={question.question_text_es || ''}
          onChange={(e) => setQuestion({ ...question, question_text_es: e.target.value })}
          rows={3}
          placeholder="Introducir traducción al español..."
          data-testid="question-text-es-input"
        />
      </div>

      {/* Toggle for additional languages */}
      <div className="border-t pt-3">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          onClick={() => setExpandedLangs(!expandedLangs)}
          className="w-full justify-center text-slate-600"
        >
          {expandedLangs ? '▲ Hide' : '▼ Show'} more languages (FR, ZH, RU, IT, PL, SV, PT, NL, TR, AR, HI)
        </Button>
      </div>

      {/* Additional Languages - Collapsible */}
      {expandedLangs && (
        <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          {/* Question Text - French */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇫🇷</span>
              Question Text (French)
            </Label>
            <Textarea
              value={question.question_text_fr || ''}
              onChange={(e) => setQuestion({ ...question, question_text_fr: e.target.value })}
              rows={2}
              placeholder="Saisir la traduction française..."
              data-testid="question-text-fr-input"
            />
          </div>

          {/* Question Text - Chinese */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇨🇳</span>
              Question Text (Chinese)
            </Label>
            <Textarea
              value={question.question_text_zh || ''}
              onChange={(e) => setQuestion({ ...question, question_text_zh: e.target.value })}
              rows={2}
              placeholder="输入中文翻译..."
              data-testid="question-text-zh-input"
            />
          </div>

          {/* Question Text - Russian */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇷🇺</span>
              Question Text (Russian)
            </Label>
            <Textarea
              value={question.question_text_ru || ''}
              onChange={(e) => setQuestion({ ...question, question_text_ru: e.target.value })}
              rows={2}
              placeholder="Введите русский перевод..."
              data-testid="question-text-ru-input"
            />
          </div>

          {/* Question Text - Italian */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇮🇹</span>
              Question Text (Italian)
            </Label>
            <Textarea
              value={question.question_text_it || ''}
              onChange={(e) => setQuestion({ ...question, question_text_it: e.target.value })}
              rows={2}
              placeholder="Inserisci la traduzione italiana..."
              data-testid="question-text-it-input"
            />
          </div>

          {/* Question Text - Polish */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇵🇱</span>
              Question Text (Polish)
            </Label>
            <Textarea
              value={question.question_text_pl || ''}
              onChange={(e) => setQuestion({ ...question, question_text_pl: e.target.value })}
              rows={2}
              placeholder="Wprowadź polskie tłumaczenie..."
              data-testid="question-text-pl-input"
            />
          </div>

          {/* Question Text - Swedish */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇸🇪</span>
              Question Text (Swedish)
            </Label>
            <Textarea
              value={question.question_text_sv || ''}
              onChange={(e) => setQuestion({ ...question, question_text_sv: e.target.value })}
              rows={2}
              placeholder="Ange svensk översättning..."
              data-testid="question-text-sv-input"
            />
          </div>

          {/* Question Text - Portuguese */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇵🇹</span>
              Question Text (Portuguese)
            </Label>
            <Textarea
              value={question.question_text_pt || ''}
              onChange={(e) => setQuestion({ ...question, question_text_pt: e.target.value })}
              rows={2}
              placeholder="Insira a tradução portuguesa..."
              data-testid="question-text-pt-input"
            />
          </div>

          {/* Question Text - Dutch */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇳🇱</span>
              Question Text (Dutch)
            </Label>
            <Textarea
              value={question.question_text_nl || ''}
              onChange={(e) => setQuestion({ ...question, question_text_nl: e.target.value })}
              rows={2}
              placeholder="Voer de Nederlandse vertaling in..."
              data-testid="question-text-nl-input"
            />
          </div>

          {/* Question Text - Turkish */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇹🇷</span>
              Question Text (Turkish)
            </Label>
            <Textarea
              value={question.question_text_tr || ''}
              onChange={(e) => setQuestion({ ...question, question_text_tr: e.target.value })}
              rows={2}
              placeholder="Türkçe çeviriyi girin..."
              data-testid="question-text-tr-input"
            />
          </div>

          {/* Question Text - Arabic */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇸🇦</span>
              Question Text (Arabic)
            </Label>
            <Textarea
              value={question.question_text_ar || ''}
              onChange={(e) => setQuestion({ ...question, question_text_ar: e.target.value })}
              rows={2}
              placeholder="أدخل الترجمة العربية..."
              data-testid="question-text-ar-input"
              dir="rtl"
            />
          </div>

          {/* Question Text - Hindi */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <span className="text-lg">🇮🇳</span>
              Question Text (Hindi)
            </Label>
            <Textarea
              value={question.question_text_hi || ''}
              onChange={(e) => setQuestion({ ...question, question_text_hi: e.target.value })}
              rows={2}
              placeholder="हिंदी अनुवाद दर्ज करें..."
              data-testid="question-text-hi-input"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Question Type</Label>
          <Select
            value={question.question_type}
            onValueChange={(v) => {
              const newState = { ...question, question_type: v, correct_answers: [] };
              if (v === 'match' && (!question.match_pairs || question.match_pairs.length === 0)) {
                newState.match_pairs = [
                  { left: '', right: '' },
                  { left: '', right: '' },
                  { left: '', right: '' },
                  { left: '', right: '' }
                ];
              }
              if (v === 'truefalse') {
                newState.choices = [];
                newState.correct_answers = [];
              }
              setQuestion(newState);
            }}
          >
            <SelectTrigger data-testid="question-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single Choice</SelectItem>
              <SelectItem value="multi">Multiple Choice</SelectItem>
              <SelectItem value="truefalse">True / False</SelectItem>
              <SelectItem value="fill">Fill-in-the-blank</SelectItem>
              <SelectItem value="match">Matching</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={question.category}
            onValueChange={(v) => setQuestion({ ...question, category: v })}
          >
            <SelectTrigger data-testid="category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Difficulty</Label>
          <Select
            value={question.difficulty}
            onValueChange={(v) => setQuestion({ ...question, difficulty: v })}
          >
            <SelectTrigger data-testid="difficulty-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">Easy</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="hard">Hard</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Cognitive Level
            <Badge variant="secondary" className="text-xs font-normal">Bloom</Badge>
          </Label>
          <Select
            value={question.cognitive_level || 'none'}
            onValueChange={(v) => setQuestion({ ...question, cognitive_level: v === 'none' ? null : v })}
          >
            <SelectTrigger data-testid="cognitive-level-select">
              <SelectValue placeholder="Select cognitive level..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Not set --</SelectItem>
              <SelectItem value="recall">Recall (Erinnern)</SelectItem>
              <SelectItem value="apply">Apply (Anwenden)</SelectItem>
              <SelectItem value="analyze">Analyze (Analysieren)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {(question.question_type === 'single' || question.question_type === 'multi' || question.question_type === 'multiple') && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Answer Options (click to mark as correct)</Label>
            <Button type="button" variant="outline" size="sm" onClick={addChoice} data-testid="add-choice-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Answer
            </Button>
          </div>
          {question.choices.map((choice, idx) => (
            <div key={choice.id} className="space-y-1 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
              {/* Main answer (English) */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleCorrectAnswer(choice.id)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                    question.correct_answers?.includes(choice.id)
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {String.fromCharCode(65 + idx)}
                </button>
                <span className="text-sm">🇬🇧</span>
                <Input
                  value={choice.text}
                  onChange={(e) => updateChoice(idx, 'text', e.target.value)}
                  placeholder={`Answer ${String.fromCharCode(65 + idx)} (English)`}
                  data-testid={`choice-${idx}-input`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeChoice(idx)}
                  className="text-red-500 hover:text-red-700"
                  disabled={question.choices.length <= 2}
                  data-testid={`remove-choice-${idx}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {/* German */}
              <div className="flex items-center gap-2 ml-10 mr-10">
                <span className="text-sm">🇩🇪</span>
                <Input
                  value={choice.text_de || ''}
                  onChange={(e) => updateChoice(idx, 'text_de', e.target.value)}
                  placeholder={`German`}
                  className="text-sm"
                  data-testid={`choice-${idx}-de-input`}
                />
              </div>
              {/* Spanish */}
              <div className="flex items-center gap-2 ml-10 mr-10">
                <span className="text-sm">🇪🇸</span>
                <Input
                  value={choice.text_es || ''}
                  onChange={(e) => updateChoice(idx, 'text_es', e.target.value)}
                  placeholder={`Spanish`}
                  className="text-sm"
                  data-testid={`choice-${idx}-es-input`}
                />
              </div>
              {/* Additional languages (collapsible based on expandedLangs) */}
              {expandedLangs && (
                <>
                  <div className="flex items-center gap-2 ml-10 mr-10">
                    <span className="text-sm">🇫🇷</span>
                    <Input
                      value={choice.text_fr || ''}
                      onChange={(e) => updateChoice(idx, 'text_fr', e.target.value)}
                      placeholder={`French`}
                      className="text-sm"
                      data-testid={`choice-${idx}-fr-input`}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-10 mr-10">
                    <span className="text-sm">🇨🇳</span>
                    <Input
                      value={choice.text_zh || ''}
                      onChange={(e) => updateChoice(idx, 'text_zh', e.target.value)}
                      placeholder={`Chinese`}
                      className="text-sm"
                      data-testid={`choice-${idx}-zh-input`}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-10 mr-10">
                    <span className="text-sm">🇷🇺</span>
                    <Input
                      value={choice.text_ru || ''}
                      onChange={(e) => updateChoice(idx, 'text_ru', e.target.value)}
                      placeholder={`Russian`}
                      className="text-sm"
                      data-testid={`choice-${idx}-ru-input`}
                    />
                  </div>
                  <div className="flex items-center gap-2 ml-10 mr-10">
                    <span className="text-sm">🇮🇹</span>
                    <Input
                      value={choice.text_it || ''}
                      onChange={(e) => updateChoice(idx, 'text_it', e.target.value)}
                      placeholder={`Italian`}
                      className="text-sm"
                      data-testid={`choice-${idx}-it-input`}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
          <p className="text-xs text-slate-500">
            Minimum 2 answers. You can have up to {question.choices.length} answers. Empty answers will not be saved.
          </p>
        </div>
      )}

      {question.question_type === 'truefalse' && (
        <div className="space-y-3">
          <Label>Correct Answer</Label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setQuestion({ ...question, correct_answers: ['true'] })}
              className={`p-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                question.correct_answers?.[0] === 'true'
                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-green-500/50'
              }`}
              data-testid="truefalse-correct-true"
            >
              <CheckCircle className={`w-5 h-5 ${question.correct_answers?.[0] === 'true' ? 'text-green-500' : 'text-slate-400'}`} />
              <span className="font-medium">True</span>
            </button>
            <button
              type="button"
              onClick={() => setQuestion({ ...question, correct_answers: ['false'] })}
              className={`p-4 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                question.correct_answers?.[0] === 'false'
                  ? 'border-red-500 bg-red-50 dark:bg-red-950/30'
                  : 'border-slate-200 dark:border-slate-700 hover:border-red-500/50'
              }`}
              data-testid="truefalse-correct-false"
            >
              <XCircle className={`w-5 h-5 ${question.correct_answers?.[0] === 'false' ? 'text-red-500' : 'text-slate-400'}`} />
              <span className="font-medium">False</span>
            </button>
          </div>
          <p className="text-sm text-slate-500">
            Select whether the statement in the question is true or false.
          </p>
        </div>
      )}

      {question.question_type === 'fill' && (
        <div className="space-y-2">
          <Label>Correct Answers (comma-separated for multiple accepted answers)</Label>
          <Input
            value={question.correct_answers?.join(', ') || ''}
            onChange={(e) => setQuestion({
              ...question,
              correct_answers: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
            })}
            placeholder="e.g. Answer1, Answer2"
            data-testid="fill-answers-input"
          />
        </div>
      )}

      {question.question_type === 'match' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Match Pairs (Left ↔ Right)</Label>
            <Button type="button" variant="outline" size="sm" onClick={addMatchPair} data-testid="add-match-pair-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Pair
            </Button>
          </div>
          <p className="text-sm text-slate-500">
            Enter the matching terms. The left column will be shown to participants, the right column must be matched.
          </p>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 text-sm font-medium text-slate-600 dark:text-slate-400">
              <span>Term</span>
              <span></span>
              <span>Match</span>
              <span></span>
            </div>
            {(question.match_pairs || []).map((pair, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center">
                <Input
                  value={pair.left || ''}
                  onChange={(e) => updateMatchPair(idx, 'left', e.target.value)}
                  placeholder={`Term ${idx + 1}`}
                  data-testid={`match-left-${idx}`}
                />
                <span className="text-slate-400">↔</span>
                <Input
                  value={pair.right || ''}
                  onChange={(e) => updateMatchPair(idx, 'right', e.target.value)}
                  placeholder={`Match ${idx + 1}`}
                  data-testid={`match-right-${idx}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMatchPair(idx)}
                  className="text-red-500 hover:text-red-700"
                  disabled={(question.match_pairs || []).length <= 2}
                  data-testid={`remove-match-${idx}`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scenario Field (Optional) */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          Scenario / Context
          <span className="text-xs text-slate-500">(optional)</span>
        </Label>
        <Textarea
          value={question.scenario || ''}
          onChange={(e) => setQuestion({ ...question, scenario: e.target.value })}
          rows={3}
          placeholder="Describe the situation or context for this question (e.g., 'A project manager is working on a complex IT project when...')"
          data-testid="scenario-input"
        />
      </div>

      {/* Structured Explanations Section */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <h4 className="font-semibold text-emerald-800 dark:text-emerald-200">Structured Explanations</h4>
        </div>
        
        {/* Correct Answer Justification */}
        <div className="space-y-2 mb-4">
          <Label className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            Why is the correct answer right?
          </Label>
          <Textarea
            value={question.correct_justification || ''}
            onChange={(e) => setQuestion({ ...question, correct_justification: e.target.value })}
            rows={3}
            placeholder="Explain why the correct answer(s) is/are right according to the official source..."
            className="border-emerald-300 focus:border-emerald-500"
            data-testid="correct-justification-input"
          />
        </div>

        {/* Incorrect Explanations - One for each wrong answer */}
        {(() => {
          const validTypes = ['single', 'multi', 'multiple', 'single_choice', 'multi_choice', 'multiple_choice'];
          const typeMatch = validTypes.includes(question.question_type?.toLowerCase());
          const hasChoices = question.choices && question.choices.length > 0;
          const hasIncorrectExplanations = question.incorrect_explanations && Object.keys(question.incorrect_explanations).length > 0;
          // Show if we have choices OR if we have incorrect_explanations already set
          return typeMatch && (hasChoices || hasIncorrectExplanations);
        })() && (
          <div className="space-y-3">
            <Label className="text-red-700 dark:text-red-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Why are the incorrect answers wrong?
            </Label>
            {/* Use choices if available, otherwise fall back to incorrect_explanations keys */}
            {(question.choices && question.choices.length > 0) ? (
              question.choices.map((choice, idx) => {
                const letter = String.fromCharCode(65 + idx);
                const isCorrect = choice.is_correct || 
                                 question.correct_answers?.includes(choice.id) || 
                                 question.correct_answers?.includes(letter);
                if (isCorrect) return null;
                
                return (
                  <div key={choice.id || letter} className="flex items-start gap-2">
                    <span className="w-8 h-8 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-sm font-medium text-red-600 dark:text-red-400 shrink-0 mt-1">
                      {letter}
                    </span>
                    <Textarea
                      value={(question.incorrect_explanations || {})[letter] || ''}
                      onChange={(e) => setQuestion({
                        ...question,
                        incorrect_explanations: {
                          ...(question.incorrect_explanations || {}),
                          [letter]: e.target.value
                        }
                      })}
                      placeholder={`Explain why "${(choice.text || '').substring(0, 30)}..." is incorrect...`}
                      className="border-red-200 focus:border-red-400 min-h-[80px]"
                      rows={2}
                      data-testid={`incorrect-explanation-${letter}-input`}
                    />
                  </div>
                );
              })
            ) : (
              // Fallback: render from incorrect_explanations keys directly
              Object.entries(question.incorrect_explanations || {}).map(([letter, explanation]) => (
                <div key={letter} className="flex items-start gap-2">
                  <span className="w-8 h-8 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-sm font-medium text-red-600 dark:text-red-400 shrink-0 mt-1">
                    {letter}
                  </span>
                  <Textarea
                    value={explanation || ''}
                    onChange={(e) => setQuestion({
                      ...question,
                      incorrect_explanations: {
                        ...(question.incorrect_explanations || {}),
                        [letter]: e.target.value
                      }
                    })}
                    placeholder={`Explain why option ${letter} is incorrect...`}
                    className="border-red-200 focus:border-red-400 min-h-[80px]"
                    rows={2}
                    data-testid={`incorrect-explanation-${letter}-input`}
                  />
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Reference Section */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-blue-800 dark:text-blue-200">Reference / Source</h4>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-blue-700 dark:text-blue-300">Source Document</Label>
            <Input
              value={(question.reference || {}).source || ''}
              onChange={(e) => setQuestion({
                ...question,
                reference: { ...(question.reference || {}), source: e.target.value }
              })}
              placeholder="e.g., Scrum Guide 2020, PMBOK 7th Ed"
              className="border-blue-300 focus:border-blue-500"
              data-testid="reference-source-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-blue-700 dark:text-blue-300">Section / Page</Label>
            <Input
              value={(question.reference || {}).section || ''}
              onChange={(e) => setQuestion({
                ...question,
                reference: { ...(question.reference || {}), section: e.target.value }
              })}
              placeholder="e.g., Sprint Events, Chapter 3"
              className="border-blue-300 focus:border-blue-500"
              data-testid="reference-section-input"
            />
          </div>
        </div>
      </div>

      {/* Legacy Explanation Field (for backward compatibility) */}
      <details className="mt-4">
        <summary className="cursor-pointer text-sm text-slate-500 hover:text-slate-700">
          Legacy: Combined Explanation Field (click to expand)
        </summary>
        <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <p className="text-xs text-slate-500 mb-2">This field is kept for backward compatibility. New questions should use the structured fields above.</p>
          <RichTextEditor
            value={question.explanation || ''}
            onChange={(value) => setQuestion({ ...question, explanation: value })}
            placeholder="Combined explanation (legacy format)..."
            minHeight="100px"
            data-testid="explanation-input"
          />
        </div>
      </details>

      {/* Translation Note */}
      <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
        <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
          <Languages className="w-4 h-4" />
          Use the <strong>Translate</strong> button above to auto-translate this question to all 25 languages.
        </p>
      </div>
    </div>
  );
};

const AdminQuestionsTab = ({
  questions,
  stats,
  categories,
  searchTerm,
  setSearchTerm,
  filterCategory,
  setFilterCategory,
  isAddDialogOpen,
  setIsAddDialogOpen,
  isEditDialogOpen,
  setIsEditDialogOpen,
  newQuestion,
  setNewQuestion,
  editingQuestion,
  setEditingQuestion,
  importLoading,
  exportLoading,
  importResult,
  setImportResult,
  handleCreateQuestion,
  handleUpdateQuestion,
  handleDeleteQuestion,
  handleImportCSV,
  handleExportCSV,
  resetNewQuestion,
  getAuthHeaders,
  refreshQuestions
}) => {
  const [isSmartImportOpen, setIsSmartImportOpen] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [generatingIds, setGeneratingIds] = useState(false);
  // Preview state
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  const filteredQuestions = (questions || []).filter(q => {
    const questionText = q.question_text || q.questionText || '';
    const matchesSearch = questionText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || q.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Count questions without internal_id
  const questionsWithoutId = (questions || []).filter(q => !q.internal_id || q.internal_id === '-' || q.internal_id === '').length;

  // Generate missing internal IDs
  const handleGenerateIds = async () => {
    setGeneratingIds(true);
    try {
      const response = await fetch(`${API}/admin/generate-internal-ids`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.updated > 0) {
          toast.success(`${result.updated} IDs generiert! Kategorien: ${JSON.stringify(result.categories)}`);
          if (refreshQuestions) {
            refreshQuestions();
          }
        } else {
          toast.info('Alle Fragen haben bereits IDs');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Generieren der IDs');
      }
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    } finally {
      setGeneratingIds(false);
    }
  };

  // Handler for when a question is translated
  const handleTranslateComplete = (updatedQuestion) => {
    if (editingQuestion) {
      setEditingQuestion(updatedQuestion);
    }
    if (refreshQuestions) {
      refreshQuestions();
    }
  };

  // Toggle single question selection
  const toggleQuestionSelection = (questionId) => {
    const newSelection = new Set(selectedQuestions);
    if (newSelection.has(questionId)) {
      newSelection.delete(questionId);
    } else {
      newSelection.add(questionId);
    }
    setSelectedQuestions(newSelection);
  };

  // Toggle all questions
  const toggleAllQuestions = () => {
    if (selectedQuestions.size === filteredQuestions.length) {
      setSelectedQuestions(new Set());
    } else {
      setSelectedQuestions(new Set(filteredQuestions.map(q => q.question_id)));
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedQuestions.size === 0) return;
    
    const confirmMessage = `Möchten Sie wirklich ${selectedQuestions.size} Frage${selectedQuestions.size > 1 ? 'n' : ''} löschen?`;
    if (!confirm(confirmMessage)) return;
    
    setBulkDeleteLoading(true);
    let deletedCount = 0;
    let failedCount = 0;
    
    for (const questionId of selectedQuestions) {
      try {
        const res = await fetch(`${API}/questions/${questionId}`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        if (res.ok) {
          deletedCount++;
        } else {
          failedCount++;
        }
      } catch (error) {
        failedCount++;
      }
    }
    
    setBulkDeleteLoading(false);
    setSelectedQuestions(new Set());
    
    if (deletedCount > 0) {
      toast.success(`${deletedCount} Frage${deletedCount > 1 ? 'n' : ''} gelöscht`);
      if (refreshQuestions) {
        refreshQuestions();
      }
    }
    if (failedCount > 0) {
      toast.error(`${failedCount} Frage${failedCount > 1 ? 'n' : ''} konnten nicht gelöscht werden`);
    }
  };

  return (
    <>
      {/* Questions by Category */}
      {stats?.questions_by_category && (
        <Card className="bg-white dark:bg-slate-900 mb-4">
          <CardHeader>
            <CardTitle>Questions by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.questions_by_category).map(([category, count]) => (
                <div key={category} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <span className="font-medium">{category}:</span> {count}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question Management */}
      <Card className="bg-white dark:bg-slate-900">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <CardTitle>Question Management</CardTitle>
              {selectedQuestions.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {selectedQuestions.size} ausgewählt
                  </Badge>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading}
                    data-testid="bulk-delete-btn"
                  >
                    {bulkDeleteLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    {selectedQuestions.size} löschen
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedQuestions(new Set())}
                    data-testid="clear-selection-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Auswahl aufheben
                  </Button>
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Export Button */}
              <Button 
                variant="outline" 
                onClick={() => handleExportCSV(filterCategory || null)}
                disabled={exportLoading}
                data-testid="export-csv-btn"
              >
                {exportLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                CSV Export
              </Button>
              
              {/* Standard Import Button */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  className="hidden"
                  data-testid="import-csv-input"
                />
                <Button variant="outline" disabled={importLoading} asChild>
                  <span>
                    {importLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    CSV Import
                  </span>
                </Button>
              </label>
              
              {/* Smart Import Button */}
              <Button 
                variant="outline"
                onClick={() => setIsSmartImportOpen(true)}
                className="border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB]/10"
                data-testid="smart-import-btn"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Smart Import
              </Button>

              {/* Generate IDs Button - always visible, changes state based on need */}
              <Button
                variant="outline"
                onClick={handleGenerateIds}
                disabled={generatingIds}
                className={questionsWithoutId > 0 
                  ? "border-amber-500 text-amber-700 hover:bg-amber-50" 
                  : "border-gray-300 text-gray-500"}
                data-testid="generate-ids-btn"
              >
                {generatingIds ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : questionsWithoutId > 0 ? (
                  <AlertCircle className="w-4 h-4 mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {questionsWithoutId > 0 ? `${questionsWithoutId} IDs generieren` : 'IDs prüfen'}
              </Button>
              
              {/* Help Button - CSV Format Info */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="CSV Format Documentation"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>CSV Format Documentation</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    <p className="text-slate-600">
                      Use this format to import/export questions with translations in all 25 languages.
                    </p>
                    
                    <div>
                      <h4 className="font-semibold mb-2">Required Columns:</h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><code className="bg-slate-100 px-1 rounded">question_text</code> - English question text</li>
                        <li><code className="bg-slate-100 px-1 rounded">question_type</code> - single, multi, fill, or match</li>
                        <li><code className="bg-slate-100 px-1 rounded">category</code> - PMP, CAPM, PSM, or PSPO</li>
                        <li><code className="bg-slate-100 px-1 rounded">correct_answers</code> - Pipe-separated (c0|c1 or A|B)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2 text-emerald-700">Structured Explanations (Recommended):</h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><code className="bg-emerald-100 px-1 rounded">correct_justification</code> - Why is the correct answer right?</li>
                        <li><code className="bg-red-100 px-1 rounded">incorrect_explanation_A</code> - Why option A is wrong</li>
                        <li><code className="bg-red-100 px-1 rounded">incorrect_explanation_B</code> - Why option B is wrong</li>
                        <li><code className="bg-red-100 px-1 rounded">incorrect_explanation_C</code> - Why option C is wrong</li>
                        <li><code className="bg-red-100 px-1 rounded">incorrect_explanation_D</code> - Why option D is wrong</li>
                        <li><code className="bg-slate-100 px-1 rounded">explanation</code> - Legacy explanation text (optional)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2 text-blue-700">Reference / Source (Recommended):</h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><code className="bg-blue-100 px-1 rounded">reference_source</code> - Source document (e.g., "PMBOK Guide - 7th Edition")</li>
                        <li><code className="bg-blue-100 px-1 rounded">reference_section</code> - Section/Page (e.g., "Planning - Schedule Management")</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Optional Metadata:</h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><code className="bg-slate-100 px-1 rounded">performance_domain</code> - For PMP/CAPM (e.g., "Stakeholders", "Team", "Planning")</li>
                        <li><code className="bg-slate-100 px-1 rounded">difficulty</code> - easy, medium, or hard</li>
                        <li><code className="bg-slate-100 px-1 rounded">cognitive_level</code> - recall, apply, or analyze (Bloom)</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Choice Columns (up to 6):</h4>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><code className="bg-slate-100 px-1 rounded">choice_0</code>, <code className="bg-slate-100 px-1 rounded">choice_1</code>, ... <code className="bg-slate-100 px-1 rounded">choice_5</code></li>
                        <li><code className="bg-slate-100 px-1 rounded">choice_0_de</code>, <code className="bg-slate-100 px-1 rounded">choice_0_es</code>, etc. for translations</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Translation Columns (25 languages):</h4>
                      <div className="grid grid-cols-2 gap-2 text-slate-600">
                        <div>
                          <p className="font-medium">Question Text:</p>
                          <code className="text-xs bg-slate-100 px-1 rounded">question_text_de, question_text_es, question_text_fr, question_text_zh, question_text_ru, question_text_it, question_text_pl, question_text_sv, question_text_pt, question_text_nl, question_text_tr, question_text_ar, question_text_hi, question_text_cs, question_text_ro, question_text_el, question_text_da, question_text_no, question_text_fi, question_text_bg, question_text_hr, question_text_sk, question_text_sr, question_text_hu, question_text_uk</code>
                        </div>
                        <div>
                          <p className="font-medium">Explanation:</p>
                          <code className="text-xs bg-slate-100 px-1 rounded">explanation_de, explanation_es, explanation_fr, explanation_zh, explanation_ru, explanation_it, explanation_pl, explanation_sv, explanation_pt, explanation_nl, explanation_tr, explanation_ar, explanation_hi, explanation_cs, explanation_ro, explanation_el, explanation_da, explanation_no, explanation_fi, explanation_bg, explanation_hr, explanation_sk, explanation_sr, explanation_hu, explanation_uk</code>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="font-medium text-blue-800">Tip:</p>
                      <p className="text-blue-700">Export existing questions first to see the exact format, then modify and re-import.</p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0047AB] hover:bg-[#003380] text-white" data-testid="add-question-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Question
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Question</DialogTitle>
                  </DialogHeader>
                  <QuestionForm
                    question={newQuestion}
                    setQuestion={setNewQuestion}
                    categories={categories}
                    getAuthHeaders={getAuthHeaders}
                    onTranslateComplete={handleTranslateComplete}
                  />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateQuestion} className="bg-[#0047AB] hover:bg-[#003380]">
                      Create Question
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Import Result */}
          {importResult && (
            <div className={`mb-4 p-4 rounded-lg ${
              importResult.error 
                ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900'
                : 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900'
            }`}>
              <div className="flex items-start gap-3">
                {importResult.error ? (
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                )}
                <div className="flex-1">
                  {importResult.error ? (
                    <p className="text-red-700 dark:text-red-400">{importResult.error}</p>
                  ) : (
                    <>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        {importResult.message}
                      </p>
                      {importResult.error_count > 0 && (
                        <div className="mt-2">
                          <p className="text-sm text-amber-600 dark:text-amber-400">
                            {importResult.error_count} errors during import:
                          </p>
                          <ul className="mt-1 text-xs text-slate-600 dark:text-slate-400 max-h-32 overflow-y-auto">
                            {importResult.errors?.map((err, i) => (
                              <li key={i} className="py-0.5">{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setImportResult(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Questions Table */}
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={filteredQuestions.length > 0 && selectedQuestions.size === filteredQuestions.length}
                      onCheckedChange={toggleAllQuestions}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="w-[100px]">ID</TableHead>
                  <TableHead className="w-[30%]">Question</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Translations</TableHead>
                  <TableHead>Difficulty</TableHead>
                  <TableHead>Cognitive</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuestions.map((q) => (
                  <TableRow key={q.question_id} className={selectedQuestions.has(q.question_id) ? 'bg-blue-50 dark:bg-blue-950/20' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedQuestions.has(q.question_id)}
                        onCheckedChange={() => toggleQuestionSelection(q.question_id)}
                        data-testid={`select-${q.question_id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">
                      {q.internal_id || '-'}
                    </TableCell>
                    <TableCell className="font-medium">
                      {(q.question_text || q.questionText || '').substring(0, 60)}...
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={q.category === 'PMP' ? 'border-blue-500 text-blue-600' : 'border-green-500 text-green-600'}
                      >
                        {q.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{q.question_type}</TableCell>
                    <TableCell>
                      <TranslationStatusBadge question={q} />
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs ${
                        q.difficulty === 'easy' ? 'text-green-600' :
                        q.difficulty === 'medium' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          q.difficulty === 'easy' ? 'bg-green-500' :
                          q.difficulty === 'medium' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                        {q.difficulty === 'easy' ? 'Easy' : q.difficulty === 'medium' ? 'Medium' : 'Hard'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {q.cognitive_level ? (
                        <Badge 
                          variant="outline" 
                          className={
                            q.cognitive_level === 'recall' ? 'border-blue-400 text-blue-600 bg-blue-50' :
                            q.cognitive_level === 'apply' ? 'border-purple-400 text-purple-600 bg-purple-50' :
                            'border-orange-400 text-orange-600 bg-orange-50'
                          }
                        >
                          {q.cognitive_level === 'recall' ? 'Recall' : 
                           q.cognitive_level === 'apply' ? 'Apply' : 'Analyze'}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setPreviewQuestion(q);
                            setIsPreviewOpen(true);
                          }}
                          title="Preview question as user sees it"
                          data-testid={`preview-${q.question_id}`}
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingQuestion(normalizeQuestionForForm(q));
                            setIsEditDialogOpen(true);
                          }}
                          data-testid={`edit-${q.question_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteQuestion(q.question_id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-${q.question_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Question</DialogTitle>
          </DialogHeader>
          {editingQuestion && (
            <QuestionForm
              question={editingQuestion}
              setQuestion={setEditingQuestion}
              categories={categories}
              getAuthHeaders={getAuthHeaders}
              onTranslateComplete={handleTranslateComplete}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateQuestion} className="bg-[#0047AB] hover:bg-[#003380]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog - Shows question as user sees it */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Question Preview (User View)
            </DialogTitle>
          </DialogHeader>
          {previewQuestion && (
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border">
              <QuestionRenderer
                question={previewQuestion}
                showExplanation={true}
                selectedAnswer={previewQuestion.correct_answers?.[0]}
                isCorrect={true}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setIsPreviewOpen(false);
                setEditingQuestion(normalizeQuestionForForm(previewQuestion));
                setIsEditDialogOpen(true);
              }}
              className="bg-[#0047AB] hover:bg-[#003380]"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Smart CSV Import Modal */}
      <CSVColumnMapper
        isOpen={isSmartImportOpen}
        onClose={() => setIsSmartImportOpen(false)}
        getAuthHeaders={getAuthHeaders}
        onImportComplete={refreshQuestions}
      />
    </>
  );
};

export default AdminQuestionsTab;
